import React, { FC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import axios from "axios";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { editStockTransaction } from "../../Services/inventoryService";
import { LBL_CANCEL, LBL_SAVE } from "../../Constants/LabelConstants";
import { MSG_STOCK_CANNOT_BE_NEGATIVE, MSG_STOCK_ERROR, MSG_STOCK_UPDATED, MSG_STOCKIN_CANNOT_BE_NEGATIVE, MSG_STOCKOUT_CANNOT_BE_NEGATIVE } from "../../Constants/MessageConstant";
interface EditStockPopupProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editData?: {
    transactionId: string;
    date: Date | null;
    stock: number;
    stockOut: number;
    stockIn: number;
    itemId: string;
  } | null;
  onUpdate: () => void;
}

interface FormData {
  date: string;
  stock: string;
  stockOut: string;
  stockIn: string;
}

interface FormErrors {
  [key: string]: string;
}

const EditStockPopup: FC<EditStockPopupProps> = ({
  open,
  setOpen,
  editData,
  onUpdate,
}) => {
  const [formData, setFormData] = useState<FormData>({
    date: "",
    stock: "",
    stockOut: "",
    stockIn: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [date, setDate] = useState<Dayjs | null>(null);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const orgId = localStorage.getItem("orgId") || "";  
  useEffect(() => {
    if (open && editData && editData.date) {
      const parsedDate = dayjs(editData.date);
      if (parsedDate.isValid()) {
        setDate(parsedDate);
        setFormData({
          date: parsedDate.format("MM-DD-YYYY"),
          stock: editData.stock?.toString() || "",
          stockOut: editData.stockOut?.toString() || "",
          stockIn: editData.stockIn?.toString() || "",
        });
      }
    } else if (open) {
      resetForm();
    }
  }, [open, editData]); // 👈 Add `open` as a dependency
  
  const resetForm = () => {
    setFormData({
      date: "",
      stock: "",
      stockOut: "",
      stockIn: "",
    });
    setDate(null);
    setErrors({});
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (value.trim() !== "") {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    setDate(newValue);
    if (newValue) {
      const formatted = dayjs(newValue).format("MM-DD-YYYY");
      setFormData((prev) => ({ ...prev, date: formatted }));
      setErrors((prev) => ({ ...prev, date: "" }));
    } else {
      setFormData((prev) => ({ ...prev, date: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    // if (!formData.date) newErrors.date = "Date is required";
    // if (!formData.stock) newErrors.stock = "Stock is required";
    // if (!formData.stockIn) newErrors.stockIn = "Stock In is required";
    // if (!formData.stockOut) newErrors.stockOut = "Stock Out is required";

    if (Number(formData.stock) < 0)
      newErrors.stock = MSG_STOCK_CANNOT_BE_NEGATIVE;
    if (Number(formData.stockIn) < 0)
      newErrors.stockIn = MSG_STOCKIN_CANNOT_BE_NEGATIVE;
    if (Number(formData.stockOut) < 0)
      newErrors.stockOut = MSG_STOCKOUT_CANNOT_BE_NEGATIVE;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    resetForm();
    setOpen(false);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      orgId,
      transactionId: editData?.transactionId ?? "",
      date: dayjs(date).format("YYYY-MM-DD"), // Send date in correct format
      stock: Number(formData.stock),
      stockIn: Number(formData.stockIn),
      stockOut: Number(formData.stockOut),
      updatedBy: "AdminUser", // Replace with real user
      createdByIp: "192.168.1.1", // Replace with real IP
      itemId: editData?.itemId ?? ""
    };
    try {
      setSaving(true);
      await editStockTransaction(payload);
      enqueueSnackbar(MSG_STOCK_UPDATED, { variant: "success" });
      onUpdate();
      handleClose();

    } catch (error) {
      let message = MSG_STOCK_ERROR;
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      enqueueSnackbar(message, { variant: "error" });
    }
    finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: { maxHeight: "80vh", marginTop: "-10vh" } }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          textDecoration: "underline",
        }}
      >
        Edit Stock
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                value={date}
                onChange={handleDateChange}
                minDate={dayjs(new Date(0))}
                maxDate={dayjs(new Date())}
                slotProps={{
                  textField: {
                    
                    fullWidth: true,
                    error: !!errors.date,
                    helperText: errors.date,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={6}>
            <TextField
              type="text"
              label="Stock"
              name="stock"
              value={formData.stock}
              onChange={(e) => {
                const value = e.target.value;

                const isValid =
                  /^(\d{0,10})$/.test(value) || // Whole numbers up to 10 digits
                  (/^\d{1,10}(\.\d{0,2})?$/.test(value) && (value.replace(/\D/g, "").length <= 10));

                if (isValid) {
                  handleChange(e);
                }
              }}
              onKeyDown={(e) => {
                if (["+", "-", "e", "E"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              fullWidth
              error={!!errors.stock}
              helperText={errors.stock}
            />

          </Grid>
          <Grid item xs={6}>
            <TextField
              type="text"
              label="Stock In"
              name="stockIn"
              value={formData.stockIn}
              onChange={(e) => {
                const value = e.target.value;

                const isValid =
                  /^(\d{0,10})$/.test(value) || // Whole numbers up to 10 digits
                  (/^\d{1,10}(\.\d{0,2})?$/.test(value) && (value.replace(/\D/g, "").length <= 10));

                if (isValid) {
                  handleChange(e);
                }
              }}
              onKeyDown={(e) => {
                if (["+", "-", "e", "E"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              fullWidth
              error={!!errors.stock}
              helperText={errors.stock}
            />
          </Grid>
          <Grid item xs={6}>            
             <TextField
              type="text"
              label="Stock Out"
              name="stockOut"
              value={formData.stockOut}
              onChange={(e) => {
                const value = e.target.value;
                const isValid =
                  /^(\d{0,10})$/.test(value) || // Whole numbers up to 10 digits
                  (/^\d{1,10}(\.\d{0,2})?$/.test(value) && (value.replace(/\D/g, "").length <= 10));

                if (isValid) {
                  handleChange(e);
                }
              }}
              onKeyDown={(e) => {
                if (["+", "-", "e", "E"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              fullWidth
              error={!!errors.stock}
              helperText={errors.stock}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{LBL_CANCEL}</Button>
        {/* <Button variant="contained" onClick={handleSave}>
          Save
        </Button> */}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {/* {saving ? "Saving..." : "Save"} */}
          {saving ? <CircularProgress size={20} color="inherit" /> : LBL_SAVE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditStockPopup;
